// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { resolveState } from '../../types/bulk_action_log'
import { cents, percentage } from '../../util/formatters'
import { disableInputs } from '../../util/columnResolver'
import { TableName } from '../../config'
import { axiosPatch, axiosPost } from '../../util/api'
import { DefaultSelected } from '../../packages/blueprint-components/src/common'
import { Toasts } from '../toaster/Toast'
import { noCase, pascalCase } from 'change-case'
import { VirtualizedTableWithoutBulkActions } from './VirtualizedTableWithoutBulkActions'
import { idKey } from '../../types/constants'
import { OutOfSyncReviewView } from './OutOfSyncReviewView'

export { OutOfSyncReviewView, type OutOfSyncReviewViewProps } from './OutOfSyncReviewView'

export interface BulkActionRetryAcknowledgement {
    data?: {
        message?: string
        progress?: Record<string, { uuid?: string }>
        [key: string]: unknown
    }
}

export interface OutOfSyncModalProps {
    headerVerbiage?: string
    isOpen?: boolean
    closeOnUnknownResult?: boolean
    onClose: () => void
    columns: unknown[]
    modalData: Record<string, unknown>
    setModalData: (value: Record<string, unknown>) => void
    selectKey: string
    name: string
    outOfSyncIds: Array<string | number>
    endpoint: string
    modelName?: string | null
    payload: {
        selection: {
            all: boolean
            safeList: Array<string | number>
            blockList: Array<string | number>
            selectedCount: number
        }
        [key: string]: unknown
    }
    /** Called for an async acknowledgement; terminal refresh remains owned by the caller. */
    onRetryAcknowledged?: (
        acknowledgement: BulkActionRetryAcknowledgement,
        retryPayload: Record<string, unknown>,
    ) => void
    /** @deprecated Async acknowledgement must never refresh the source table. */
    reloadParent?: () => void
}

export const OutOfSyncModal = ({
    isOpen = false,
    closeOnUnknownResult = false,
    onClose,
    columns,
    modalData,
    setModalData,
    selectKey,
    name,
    outOfSyncIds,
    // previouslySelectedValues,
    endpoint,
    modelName = null,
    payload,
    onRetryAcknowledged,
}: OutOfSyncModalProps) => {
    const [selectedValues, setSelectedValues] = useState({ ...DefaultSelected })
    const [loading, setLoading] = useState(false)

    const unselectedValues = useMemo(() => {
        const all = payload?.selection?.all

        if (all) {
            return selectedValues?.blockList
        } else {
            // MER-2802: outOfSyncIds arrive as strings from the conflict
            // response, row ids may be numbers. Compare on String(...).
            const safeKeys = new Set(selectedValues.safeList.map(idKey))
            const outOfSyncKeys = new Set(outOfSyncIds.map(idKey))

            return outOfSyncIds
                .filter((x) => !safeKeys.has(idKey(x)))
                .concat(selectedValues.safeList.filter((x) => !outOfSyncKeys.has(idKey(x))))
        }
    }, [selectedValues])

    const [adTypeEndpoint, adTypeParam] = useMemo(() => resolveAdTypeEndpointAndParam(name), [name])

    const load = (params) => {
        setLoading(true)
        const payload = {
            ...params,
            [adTypeParam]: outOfSyncIds,
        }

        return axiosPost(adTypeEndpoint, payload)
            .then((res) => {
                setLoading(false)
                return res
            })
            .catch((err) => {
                setLoading(false)
                return err
            })
    }

    const onSubmit = () => {
        setLoading(true)

        let res = []
        let ids = {}

        // Removes unselected values
        // from the previously selected values
        if (payload?.selection?.all) {
            ids = {
                ...payload?.selection,
                ...{
                    blockList: unselectedValues,
                    selectedCount: payload?.selection.selectedCount - unselectedValues.length,
                },
            }
        } else {
            const unselectedKeys = new Set(unselectedValues.map(idKey))
            res = payload?.selection.safeList.filter((el) => {
                return !unselectedKeys.has(idKey(el))
            })

            ids = {
                ...payload?.selection,
                ...{
                    safeList: res,
                    selectedCount: res.length,
                },
            }
        }

        const newPayload = {
            ...payload,
            selection: ids,
            update: modalData,
            class: modelName ? pascalCase(modelName) : pascalCase(name),
        }

        axiosPatch(endpoint, newPayload)
            .then((res) => {
                onRetryAcknowledged?.(res, newPayload)
                setLoading(false)
                onCancel()
            })
            .catch((err) => {
                setLoading(false)
                if (
                    closeOnUnknownResult &&
                    err?.response?.status === 503 &&
                    typeof err.response.data?.request_uuid === 'string'
                ) {
                    // The edit may still finish; closing prevents a retry with a new UUID.
                    onCancel()
                    return
                }
                if (err?.response?.status === 409) {
                    // This should rarely (never?) happen
                    // MER-2802: keep the conflict keys as strings; parseInt truncates.
                    const ids = Object.keys(err.response?.data ?? {})
                    Toasts.warning(
                        ids.length +
                            ' items were out of sync. Please review the changes and retry.',
                    )
                }
            })
    }

    const onCancel = () => {
        setSelectedValues({ ...DefaultSelected })
        onClose()
        setModalData({})
        // axiosDestroy('bulk-actions', {
        //     class: modelName ? pascalCase(modelName) : pascalCase(name),
        // }).then((res) => {
        //     Toasts.success(res?.data?.data?.message)
        // }).catch((e) => {
        //     // Do nothing
        // })
    }

    return (
        <OutOfSyncReviewView
            isOpen={isOpen}
            actionDescription={resolveActionVerbiage(modalData)}
            conflictCount={outOfSyncIds.length}
            selectedCount={selectedValues.selectedCount}
            loading={loading}
            onClose={onCancel}
            onRetry={onSubmit}
        >
            <OutOfSyncTable
                isOpen={isOpen}
                columns={columns}
                selectKey={selectKey}
                outOfSyncIds={outOfSyncIds}
                previouslySelectedValues={payload?.selection}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                load={load}
            />
        </OutOfSyncReviewView>
    )
}

const OutOfSyncTable = ({
    isOpen,
    columns,
    selectKey,
    outOfSyncIds,
    previouslySelectedValues,
    selectedValues,
    setSelectedValues,
    load,
}) => {
    const disabledColumns = useMemo(() => disableInputs(columns), [isOpen, columns])

    // Modifies selectedValues and selectedCount to
    // show accurate data in the table
    useEffect(() => {
        if (outOfSyncIds.length > 0) {
            setSelectedValues({
                ...previouslySelectedValues,
                ...{
                    safeList: outOfSyncIds,
                    selectedCount: outOfSyncIds.length,
                },
            })
        }
    }, [previouslySelectedValues, outOfSyncIds])

    return (
        <VirtualizedTableWithoutBulkActions
            name="out-of-sync-modal"
            canSelect={null}
            accordion={null}
            accordionRaw={null}
            mutableFields={null}
            headerComponent={null}
            dateSelect={false}
            searchPlaceholder=""
            modelName=""
            filters={null}
            search={false}
            style={{}}
            columnSelection={false}
            showHeader={true}
            tableHooks={null}
            load={load}
            columns={disabledColumns}
            selectedValues={selectedValues}
            onSelectChange={setSelectedValues}
            selectKey={selectKey}
            otherActions={null}
            extra={null}
        />
    )
}

const resolveAdTypeEndpointAndParam = (name: string) => {
    switch (name) {
        case TableName.Campaign:
            return ['campaigns', 'campaign_ids']
        case TableName.AdGroup:
            return ['ad-groups', 'ad_group_ids']
        case TableName.Target:
            return ['targets', 'target_ids']
        case TableName.NegativeTarget:
            return ['negative-targets', 'target_ids']
        case TableName.Keyword:
            return ['keywords', 'keyword_ids']
        case TableName.NegativeKeyword:
            return ['negative-keywords', 'keyword_ids']
        case TableName.CampaignNegativeKeyword:
            return ['campaign-negative-keywords', 'keyword_ids']
        case TableName.ProductAds:
            return ['product-ads', 'ad_ids']
        default:
            return [null, null]
    }
}

export const resolveActionVerbiage = (modalData) => {
    const prefix = 'You were trying to '
    let dir = 'increase'

    let k = hasKey(modalData, ['default_bid', 'bid', 'daily_budget'])
    if (k) {
        const { key, formatted } = k
        return `${prefix} set the ${formatted} to ${cents(modalData[key])}`
    }

    k = hasKey(modalData, ['default_bid_percent', 'bid_percent', 'daily_budget_percent'])
    if (k) {
        const { key, formatted } = k
        if (modalData[key] < 0) {
            dir = 'decrease'
        }
        return `${prefix} ${dir} the ${formatted} by ${Math.abs(modalData[key])}%`
    }

    k = hasKey(modalData, ['default_bid_delta', 'bid_delta', 'daily_budget_delta'])
    if (k) {
        const { key, formatted } = k
        if (modalData[key] < 0) {
            dir = 'decrease'
        }
        return `${prefix} ${dir} the ${formatted} by ${cents(Math.abs(modalData[key]))}`
    }

    switch (true) {
        case Object.hasOwn(modalData, 'state'):
            return `${prefix} set the state to ${resolveState(modalData.state)}`
        case Object.hasOwn(modalData, 'start_date'):
            return `${prefix} set the start date to ${date(modalData.start_date)}` // TODO fix
        case Object.hasOwn(modalData, 'end_date'):
            return `${prefix} set the end date to ${date(modalData.end_date)}` // TODO fix
        case Object.hasOwn(modalData, 'name'):
            return `${prefix} set the name to ${date(modalData.name)}`
        case Object.hasOwn(modalData, 'premium_bid_adjustment'):
            return `${prefix} set the premium bid adjustment to ${modalData.premium_bid_adjustment ? 'true' : 'false'}`
        case Object.hasOwn(modalData, 'target_acos'):
            return `${prefix} set the target acos to ${percentage(modalData.target_acos / 100)}`
        case Object.hasOwn(modalData, 'portfolio'):
            return `${prefix} set the assigned portfolio to ${modalData.portfolio}`
        default:
            console.warn('Cannot resolve action verbiage', modalData)
            return ''
    }
}

const hasKey = (modalData: any, keys: string[]): { key: string; formatted: string } | null => {
    const key = keys.find((k) => Object.hasOwn(modalData, k))
    if (!key) {
        return null
    }

    return {
        key,
        formatted: noCase(key),
    }
}

const date = (datetime) => new Date(datetime).toISOString().slice(0, 10).replaceAll('-', '/')
